<script setup lang="ts">
import { computed, type Component } from 'vue'
import { useHead } from '@unhead/vue'
import { useTranslate } from '@tolgee/vue'

import { BackgroundTheme, type IHeroSettings, type SocialMediaType } from '@/api/interfaces'
import { formatDate, transformUrlToSecure, optimizeCloudinaryUrl } from '@/helpers/utils'
import { usePublicData } from '@/stores/PublicData'
import { useAccountStore } from '@/stores/Account'

import LogoPlaceholderIcon from '@/components/icons/LogoPlaceholderIcon.vue'
import FacebookIcon from '@/components/icons/social-media/FacebookIcon.vue'
import TwitterIcon from '@/components/icons/social-media/TwitterIcon.vue'
import LinkedinIcon from '@/components/icons/social-media/LinkedinIcon.vue'
import TiktokIcon from '@/components/icons/social-media/TiktokIcon.vue'
import InstagramIcon from '@/components/icons/social-media/InstagramIcon.vue'
import WebsiteIcon from '@/components/icons/social-media/WebsiteIcon.vue'
import YoutubeIcon from '@/components/icons/social-media/YoutubeIcon.vue'
import BackgroundIcon from '@/components/icons/backgrounds/BackgroundIcon.vue'

interface Props {
  editorSettings: IHeroSettings
  theme?: BackgroundTheme
}

const props = withDefaults(defineProps<Props>(), {
  theme: BackgroundTheme.LIGHT,
})

const publicStore = usePublicData()
const accountStore = useAccountStore()
const { t } = useTranslate()

const socialMediaIcons: Record<SocialMediaType, Component> = {
  facebook: FacebookIcon,
  instagram: InstagramIcon,
  linkedin: LinkedinIcon,
  website: WebsiteIcon,
  twitter: TwitterIcon,
  tiktok: TiktokIcon,
  youtube: YoutubeIcon,
}

const heroImageUrl = computed(() =>
  props.editorSettings.image.visible && props.editorSettings.image.url
    ? transformUrlToSecure(props.editorSettings.image.url)
    : null,
)

const heroSrcSet = computed(() => {
  if (!heroImageUrl.value) return undefined
  const src800 = optimizeCloudinaryUrl(heroImageUrl.value, 800)
  const src1600 = optimizeCloudinaryUrl(heroImageUrl.value, 1600)
  return `${src800} 800w, ${src1600} 1600w`
})

const heroSrc = computed(() => heroImageUrl.value)

useHead(() =>
  heroSrcSet.value && heroImageUrl.value
    ? {
        link: [
          {
            rel: 'preload',
            as: 'image',
            href: optimizeCloudinaryUrl(heroImageUrl.value, 1600),
            imagesrcset: heroSrcSet.value,
            imagesizes: '100vw',
            fetchpriority: 'high',
            crossorigin: 'anonymous',
          },
        ],
      }
    : {},
)

const background = computed(() =>
  props.editorSettings.image.visible && props.editorSettings.image.url ? `transparent` : '',
)

const overlayStyle = computed(() =>
  props.editorSettings.image.visible && props.editorSettings.image.overlayColor
    ? `linear-gradient(0deg, ${props.editorSettings.image.overlayColor}95 0%, ${props.editorSettings.image.overlayColor}95 100%)`
    : 'transparent',
)

const description = computed(() => {
  if (publicStore.isBusinessAccount) {
    return (
      accountStore.account.profileDescription ||
      t.value('PublicHero.description_company', { name: publicStore.name })
    )
  } else {
    return accountStore.account.profileDescription || t.value('PublicHero.description_personal')
  }
})

const isPngImage = (url: string | undefined) => {
  return url && url.endsWith('.png')
}
</script>

<template>
  <section :class="['public-hero', editorSettings.style, theme]" :style="{ background }">
    <img
      v-if="heroSrc"
      class="public-hero-bg"
      :src="heroSrc"
      :srcset="heroSrcSet"
      sizes="100vw"
      alt=""
      width="1600"
      height="900"
      fetchpriority="high"
      decoding="async"
    />
    <div class="public-hero-overlay" :style="{ background: overlayStyle }"></div>
    <BackgroundIcon
      v-if="$route.name === 'CommunityPage'"
      class="background-icon"
      align="left"
      variant="outline"
      icon-type="leaves"
    />
    <v-container fluid>
      <div class="public-hero-wrapper">
        <div class="public-hero-header">
          <div
            v-if="
              (editorSettings.logo.url || accountStore.account.imageURL) &&
              editorSettings.logo.visible
            "
            class="public-hero-logo"
          >
            <img
              :src="editorSettings.logo.url || accountStore.account.imageURL"
              :style="
                isPngImage(editorSettings.logo.url || accountStore.account.imageURL)
                  ? 'max-width: 100%; min-width: 150px; object-fit: contain;'
                  : ''
              "
              :alt="`${publicStore.name} logo`"
              width="150"
              height="150"
              fetchpriority="high"
              decoding="async"
            />
          </div>

          <LogoPlaceholderIcon v-else class="public-hero-logo-placeholder" />
          <h1
            class="public-hero-title"
            :class="{ loading: publicStore.loading }"
            :style="{ color: editorSettings.image.textColor }"
          >
            {{ editorSettings.content[publicStore.lang]?.title }}
            <template
              v-if="!editorSettings.content[publicStore.lang]?.title && editorSettings.logo.visible"
            >
              {{ publicStore.name }}
            </template>
          </h1>
          <p
            v-if="editorSettings.memberSince.visible"
            class="public-hero-date"
            :style="{ color: editorSettings.image.textColor }"
          >
            <template v-if="$route.name === 'CommunityPage'">
              {{ $t('PublicHero.since') }}
            </template>
            <template v-else>
              {{ $t('PublicHero.member_since') }}:
              <span v-if="publicStore.signupDate">{{ formatDate(publicStore.signupDate) }}</span>
            </template>
          </p>
        </div>
        <div class="public-hero-content">
          <h2
            v-if="!publicStore.loading"
            class="public-hero-subtitle"
            :style="{ color: editorSettings.image.textColor }"
          >
            {{ editorSettings.content[publicStore.lang]?.subtitle || description }}
          </h2>
          <div v-else class="public-hero-subtitle placeholder" aria-hidden="true"></div>
          <div class="public-hero-links">
            <template v-for="(link, key) in props.editorSettings.links" :key="key">
              <a
                v-if="link && link.length"
                :href="transformUrlToSecure(link)"
                target="_blank"
                class="public-hero-link"
                rel="nofollow"
              >
                <component
                  class="public-hero-icon"
                  :is="socialMediaIcons[key]"
                  :color="editorSettings.image.textColor"
                ></component>
              </a>
            </template>
          </div>
        </div>
      </div>
    </v-container>
  </section>
</template>

<style scoped lang="scss">
.public-hero {
  position: relative;
  padding: 70px 0 40px;
  display: flex;
  align-items: center;
  text-align: center;
  background-repeat: no-repeat !important;
  background-size: cover !important;
  min-height: 480px;

  @include media('md-and-up') {
    min-height: 600px;
  }

  :deep(.background-icon) {
    display: block;
    position: absolute;
    top: -40px;
    left: -50px;
    width: 160px;
    height: 130px;
    transform: rotate(-25deg);

    @include media('md-and-up') {
      top: -80px;
      left: -80px;
      width: 480px;
      height: 390px;
    }
  }

  &.center {
    .public-hero-wrapper {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;

      @include media('sm-only') {
        max-width: 50%;
        margin: 0 auto;
      }
    }
  }

  &.side-by-side {
    .public-hero-wrapper {
      @include media('sm-and-up') {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 100px;
        text-align: left;
      }

      @include media('sm-only') {
        padding: 0 50px;
      }
    }

    .public-hero-links {
      @include media('sm-and-up') {
        justify-content: flex-start;
      }
    }

    .public-hero-header {
      flex: 0 0 auto;
    }

    .public-hero-content {
      @include media('sm-and-up') {
        max-width: 50%;
      }
    }
  }

  &.align-left {
    .public-hero-wrapper {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
      text-align: left;

      @include media('sm-and-up') {
        max-width: 50%;
      }

      @include media('sm-only') {
        padding: 0 50px;
      }
    }

    .public-hero-links {
      justify-content: flex-start;
    }
  }
}

.public-hero-wrapper {
  min-height: 370px;
}

.public-hero-bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
}

.public-hero-overlay {
  position: absolute;
  inset: 0;
  z-index: 1;
}

.public-hero-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
  min-height: 260px;
}

.public-hero-logo {
  width: 150px;
  height: 150px;
  display: flex;
  border-radius: 50%;
  overflow: hidden;
  margin-bottom: 8px;

  img {
    max-width: none;
    background-color: white;
  }
}

.public-hero-logo-placeholder {
  width: 200px;
  height: 175px;

  .community-page & {
    display: none;
  }
}

.public-hero-title {
  font-size: 32px;
  font-weight: 700;
  min-height: 72px;
  line-height: 1.2;

  @include media('md-and-up') {
    font-size: 60px;
  }

  &.loading {
    width: 100%;
    height: 37px;

    @include media('md-and-up') {
      height: 70px;
    }
  }
}

.public-hero-date {
  font-size: 20px;
  opacity: 0.75;
  margin-top: 16px;
  min-height: 24px;
}

.public-hero-content {
  position: relative;
  z-index: 2;
  min-height: 100px;
}

.public-hero-subtitle {
  font-size: 16px;
  font-weight: 700;
  max-width: 880px;
  margin: 24px auto 0 auto;
  min-height: 24px;

  @include media('md-and-up') {
    font-size: 24px;
  }
}

.public-hero-subtitle.placeholder {
  width: 100%;
  background: rgba(0, 0, 0, 0.05);
}

.public-hero-website {
  display: inline-block;
  color: var(--ui-blue);
  font-size: 24px;
  margin-top: 20px;
}

.public-hero-links {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  gap: 24px;

  a {
    cursor: pointer;
  }
}

.public-hero-icon {
  width: 24px;
  height: 24px;
}
</style>
